// API Client for REWagent Lead Management Platform

// Determine API base URL — always relative, proxied through Pages Functions
function getApiBase(): string {
  return '/api';
}

// Get stored auth token
function getAuthToken(): string | null {
  return localStorage.getItem('rewagent_token');
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Add auth token if available
  const token = getAuthToken();
  const authHeaders: Record<string, string> = {};
  if (token) {
    authHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...headers,
    },
    credentials: 'include',
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${getApiBase()}${endpoint}`, config);

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(data.error || 'An error occurred', response.status);
  }

  return data;
}

// Auth API
export const authApi = {
  login: async (accessCode: string) => {
    const data = await request<{ success: boolean; user?: import('../types').User; token?: string; error?: string }>('/auth/login', {
      method: 'POST',
      body: { access_code: accessCode },
    });
    // Store token if login successful
    if (data.success && data.token) {
      localStorage.setItem('rewagent_token', data.token);
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('rewagent_token');
    return request<{ success: boolean }>('/auth/logout', { method: 'POST' });
  },

  me: () =>
    request<{ success: boolean; user?: import('../types').User; error?: string }>('/auth/me'),
};

// Leads API
export const leadsApi = {
  list: (params: {
    status?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    campaign?: string;
    page?: number;
    limit?: number;
  } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return request<import('../types').LeadsResponse>(`/leads?${searchParams.toString()}`);
  },

  get: (id: number | string) =>
    request<import('../types').LeadDetailResponse>(`/leads/${id}`),

  updateStatus: (id: number | string, status: 'qualified' | 'disqualified', reason?: string) =>
    request<{ success: boolean; status: string }>(`/leads/${id}/status`, {
      method: 'POST',
      body: { status, reason },
    }),
};

// Admin API
export const adminApi = {
  login: (username: string, password: string) =>
    request<{ success: boolean; admin?: import('../types').AdminUser; error?: string }>('/admin/login', {
      method: 'POST',
      body: { username, password },
    }),

  loginWithAccessCode: (access_code: string) =>
    request<{ success: boolean; admin?: import('../types').AdminUser; error?: string }>('/admin/login', {
      method: 'POST',
      body: { access_code },
    }),

  logout: () =>
    request<{ success: boolean }>('/admin/logout', { method: 'POST' }),

  // Clients
  getClients: () =>
    request<import('../types').Client[]>('/admin/clients'),

  createClient: (name: string) =>
    request<{ success: boolean; client_id: string }>('/admin/clients', {
      method: 'POST',
      body: { name },
    }),

  // Team members
  getTeamMembers: (clientId: string) =>
    request<import('../types').TeamMember[]>(`/admin/clients/${clientId}/members`),

  createTeamMember: (clientId: string, name: string) =>
    request<{ success: boolean; access_code: string }>(`/admin/clients/${clientId}/members`, {
      method: 'POST',
      body: { name },
    }),

  // API keys
  getApiKeys: (clientId: string) =>
    request<import('../types').ApiKey[]>(`/admin/clients/${clientId}/api-keys`),

  createApiKey: (clientId: string, name: string) =>
    request<{ success: boolean; api_key: string }>(`/admin/clients/${clientId}/api-keys`, {
      method: 'POST',
      body: { name },
    }),

  revokeApiKey: (keyId: string) =>
    request<{ success: boolean }>(`/admin/api-keys/${keyId}`, { method: 'DELETE' }),

  // Leads
  getLeads: (params: { client_id?: string; status?: string; page?: number; limit?: number } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return request<{ data: import('../types').Lead[]; pagination: import('../types').Pagination }>(`/admin/leads?${searchParams.toString()}`);
  },

  // Admin Leads Management
  getAllLeads: (queryString: string = '') =>
    request<{ leads: any[]; pagination: { page: number; limit: number; total: number; total_pages: number } }>(`/admin/leads/all?${queryString}`),

  updateLeadStatus: (leadId: number | string, status: string, reason?: string) =>
    request<{ success: boolean; lead_id: number; status: string }>(`/admin/leads/${leadId}/status`, {
      method: 'PUT',
      body: { status, reason },
    }),

  deleteLead: (leadId: number | string) =>
    request<{ success: boolean; message: string }>(`/admin/leads/${leadId}`, { method: 'DELETE' }),

  // Activity Logs
  getLogs: (page: number = 1, limit: number = 100) =>
    request<{ logs: any[]; pagination: { page: number; limit: number; total: number; total_pages: number } }>(`/admin/logs?page=${page}&limit=${limit}`),
};

export { ApiError };
